/**
 * RequestAccessPage — route wrapper at /request-access.
 *
 * Ported from Yan-Vada Hero Pilot/components/auth-screens.jsx SignUpA (form)
 * + SignUpB (confirmation state).
 * Centered card layout on light Bawa Earth.
 * Two states: form (default) → confirmation after submit.
 *
 * Submits an access request to the backend member-approval queue.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { AuthField } from '../components/auth/shared'

const EPISODE_01_DATE = new Date(2026, 4, 8).toLocaleDateString('en-GB', {
  day: 'numeric',
  month: 'long',
})

export default function RequestAccessPage() {
  const [submitted, setSubmitted] = useState(false)
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    referral: '',
  })
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  function updateField(field: keyof typeof form) {
    return (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setForm(prev => ({ ...prev, [field]: event.target.value }))
    }
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const email = form.email.trim()
    if (email) {
      setSubmitting(true)
      setError(null)
      try {
        const res = await fetch('/api/auth/request-access', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            firstName: form.firstName,
            lastName: form.lastName,
            email,
            company: form.company,
            referral: form.referral,
          }),
        })
        const data = await res.json() as { message?: string; error?: string }
        if (!res.ok) throw new Error(data.error ?? 'Unable to submit request')
        setMessage(data.message ?? null)
        setSubmitted(true)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unable to submit request')
      } finally {
        setSubmitting(false)
      }
    }
  }

  if (submitted) {
    return (
      <div className="request-page">
        <div className="request-card">
          <div className="request-confirm-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--glow-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="5" width="18" height="14" rx="2" />
              <path d="m3 7 9 6 9-6" />
            </svg>
          </div>
          <h1 className="request-heading">Got it. We&apos;ll be in touch.</h1>
          <p className="request-sub-lg">
            {message ?? 'We read every request. If there\'s a fit for Season 01, a sign-in link will land in your inbox within a working day — usually sooner.'}
          </p>
          <div className="request-meanwhile">
            <strong>In the meantime &mdash;</strong>
            <p>
              The show drops every other Thursday on YouTube and Spotify. Episode {EPISODE_01_DATE}. If you want to get a feel before we meet, start there.
            </p>
          </div>
          <div className="request-confirm-actions">
            <Link to="/" className="btn btn-secondary">Back to vada.yan.lk</Link>
            <a href="https://youtube.com/@yanvada" target="_blank" rel="noopener noreferrer" className="btn btn-primary">Watch the trailer</a>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="request-page">
      <div className="request-card">
        <span className="legal-eyebrow">Invite only &middot; request access</span>
        <h1 className="request-heading">
          We&apos;re letting a small number of partners in at a time.
        </h1>
        <p className="request-sub">
          Tell us who you are and how you heard about the show. If there&apos;s a fit, we&apos;ll send a sign-in link within a working day.
        </p>

        <form onSubmit={handleSubmit} className="request-form">
          <div className="request-name-row">
            <AuthField name="firstName" label="First name" placeholder="Priya" value={form.firstName} onChange={updateField('firstName')} autoComplete="given-name" />
            <AuthField name="lastName" label="Last name" placeholder="Perera" value={form.lastName} onChange={updateField('lastName')} autoComplete="family-name" />
          </div>
          <AuthField
            name="email"
            label="Work email"
            type="email"
            placeholder="you@company.com"
            hint="We'll send the invite here."
            value={form.email}
            onChange={updateField('email')}
            autoComplete="email"
            required
          />
          <AuthField name="company" label="Company" placeholder="The Licenced" value={form.company} onChange={updateField('company')} autoComplete="organization" />
          <AuthField
            name="referral"
            label="How did you hear about Yan-Vada?"
            placeholder="A friend shared the pitch · Saw Riz at an event · Cold outreach · Other"
            value={form.referral}
            onChange={updateField('referral')}
            autoComplete="off"
            textarea
            rows={3}
          />
          <button type="submit" className="btn btn-primary request-submit">
            {submitting ? 'Requesting...' : 'Request access'}
          </button>
          {error && <p className="request-footer" role="alert" style={{ color: '#A84432' }}>{error}</p>}
        </form>

        <div className="request-footer">
          Already have an invite?{' '}
          <Link to="/signin" className="request-footer-link">Sign in</Link>
        </div>
      </div>
    </div>
  )
}
